import { Link } from 'react-router';
import { Calendar, MapPin, Clipboard } from 'lucide-react';

import { useEvent } from '@/contexts/EventContext';

export function HomePage() {
  const { meta } = useEvent();
  const base = `/${meta.year}`;

  return (
    <>
      {/* Hero Section with Miami Beach Background */}
      <div className="relative h-[600px] overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${meta.heroImage})`,
          }}
        >
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
        </div>

        {/* Hero Content */}
        <div className="relative flex h-full items-center justify-center px-4 text-center sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-5xl font-bold tracking-tight text-white drop-shadow-lg sm:text-6xl md:text-7xl">
              {meta.title}{' '}
              <span className="from-primary-400 to-secondary-400 bg-gradient-to-r bg-clip-text text-transparent">
                {meta.year}
              </span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-xl text-white/95 drop-shadow-md sm:text-2xl">
              {meta.tagline}
            </p>
            <div className="mt-12">
              {/* Combined Date & Location Badge */}
              <div className="relative inline-block">
                <div className="rounded-xl border border-white/20 bg-white/10 px-8 py-5 backdrop-blur-md dark:border-white/10 dark:bg-black/20 dark:backdrop-blur-lg">
                  <div className="flex items-center gap-4">
                    <Calendar className="h-10 w-10 text-white" />
                    <div className="text-left">
                      <p className="text-sm font-medium tracking-wider text-white/80 uppercase">
                        Save the Date
                      </p>
                      <p className="text-2xl font-bold text-white sm:text-3xl">
                        {meta.displayDate}
                      </p>
                      <div className="mt-2 flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-white/90" />
                        <p className="text-lg font-semibold text-white/90">
                          {meta.city}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-8 md:grid-cols-2">
          <Link
            to={`${base}/agenda`}
            className="group relative overflow-hidden rounded-lg bg-white p-6 shadow-md transition-all hover:shadow-xl dark:bg-gray-800"
          >
            <div className="mb-4">
              <div className="bg-secondary-100 dark:bg-secondary-900 inline-flex h-12 w-12 items-center justify-center rounded-lg">
                <Clipboard className="text-secondary-600 dark:text-secondary-400 h-6 w-6" />
              </div>
            </div>
            <h3 className="group-hover:text-secondary-600 dark:group-hover:text-secondary-400 text-xl font-semibold text-gray-900 dark:text-white">
              Agenda
            </h3>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              View the meeting schedule and topics to be discussed
            </p>
          </Link>

          <Link
            to={`${base}/logistics`}
            className="group relative overflow-hidden rounded-lg bg-white p-6 shadow-md transition-all hover:shadow-xl dark:bg-gray-800"
          >
            <div className="mb-4">
              <div className="bg-accent-100 dark:bg-accent-900 inline-flex h-12 w-12 items-center justify-center rounded-lg">
                <MapPin className="text-accent-600 dark:text-accent-400 h-6 w-6" />
              </div>
            </div>
            <h3 className="group-hover:text-accent-600 dark:group-hover:text-accent-400 text-xl font-semibold text-gray-900 dark:text-white">
              Logistics
            </h3>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              Find venue details, accommodations, and travel information for
              Miami
            </p>
          </Link>
        </div>
      </div>
    </>
  );
}
